import { CREATURE_POOL, getCreatureDef } from '~/data/creatures'
import { TUNING } from '~/config/tuning'
import type { Stats } from '~/types/game'

/**
 * 📅 The Daily Silhouette (v4.03, per Javi: "implement a daily mini game
 * for the silhouette game, then an additional bonus if you can guess the
 * stats") — pure logic only. Storage and reactivity live in
 * useDailyGame; everything here is a function of the DATE, so tests can
 * replay any day and every player faces the same creature.
 *
 * Economy stance (Javi's rule: farming is the drive): the whole daily
 * pays at most guessRewards[0] + statBonus ≈ one act's worth of a weak
 * clear. The streak milestones are the retention hook, and the booster
 * every 30 days is the ceremony — granted through the real pack flow.
 */

/** Deterministic PRNG (mulberry32) — same shape the puzzles use. */
function seeded(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a |= 0
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function shuffled<T>(rng: () => number, list: T[]): T[] {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

/** Local-midnight day key — the daily rolls when the PLAYER's day does. */
/** 🔐 Whether THIS device may pay today's rewards (moved here in
 * v6.34 so the decision below can be tested without Nuxt).
 * 'local' = signed out or the claim endpoint unreachable (fail open);
 * 'granted' = the account's one claim for today landed here;
 * 'duplicate' = another device already played this account's daily. */
export type DailyClaimStatus = 'unknown' | 'granted' | 'duplicate' | 'local'

/**
 * 📅 Should this device ASK the server for today's claim?
 *
 * The claim is written before a single guess is made, and the session's
 * status lives in useState — which a real page load resets. So a signed-in
 * player who refreshed an unfinished daily used to re-ask, be told the day
 * was already claimed, and have their own claim read back as "another
 * device played this": the daily closed with zero earned and the streak
 * day lost (v6.34).
 *
 * The grant is therefore remembered against the ACCOUNT as well as the
 * day. Both halves matter: the day so it expires on its own, and the uid
 * so a second account signing in on the same device cannot inherit the
 * first one's grant and skip recording its own.
 */
export function shouldRequestClaim(state: {
  /** Today's card: already finished? */
  done: boolean
  /** What this device banked for today, if anything. */
  claim?: DailyClaimStatus
  /** The account that banked it. */
  claimUid?: string | null
  /** Who is signed in NOW (null = signed out; plays device-local). */
  uid: string | null
}): boolean {
  if (state.done || !state.uid) return false
  return !(state.claim === 'granted' && state.claimUid === state.uid)
}

export function dailyKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Days since the epoch for the LOCAL calendar date. Anchored through
 * Date.UTC so every local day maps to exactly one integer — a plain
 * local-midnight getTime()/86400000 repeats or skips on the 23h/25h
 * DST days. */
export function dayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000)
}

/** The previous local day's key (for streak adjacency). */
export function yesterdayKey(date: Date): string {
  return dailyKey(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1))
}

export interface DailyPuzzle {
  day: string
  answerId: string
  /** 8 defIds (answer included), seed-shuffled. */
  choices: string[]
  /** 3 statlines (answer's included), seed-shuffled; statAnswer indexes it. */
  statChoices: Stats[]
  statAnswer: number
}

/**
 * The day's creature via ONE eternal seeded permutation, walked in a
 * cycle: ANY window of pool-length days holds every creature exactly
 * once. (A per-era reshuffle was tried first and failed its own test —
 * windows straddling an era boundary repeated 19 of 101. The eternal
 * permutation repeats its ORDER every cycle, which nobody will clock
 * across three-plus months, and the pool grows anyway.)
 */
/** 🔒 v7.59 (per Javi): the hidden creatures - Legacy and the homage five, awakened by
 * achievements (awardOnly) - are never the creature of the day, nor among its name choices
 * or statlines. From this day on: the same seed over a shorter list re-deals every answer,
 * so an earlier day keeps the pool it was dealt from (nobody's open card changes mid-day). */
export const HIDDEN_OUT_FROM = '2026-10-05'

export function buildDaily(date: Date): DailyPuzzle {
  const hide = dailyKey(date) >= HIDDEN_OUT_FROM
  const pool = CREATURE_POOL.filter(c => !(hide && c.awardOnly)).map(c => c.id).sort()
  const day = dayNumber(date)
  const perm = shuffled(seeded(0xDA11), pool)
  const answerId = perm[((day % pool.length) + pool.length) % pool.length]!
  const rng = seeded(0x511E7 + day * 104729)
  const others = shuffled(rng, pool.filter(id => id !== answerId))
  const choices = shuffled(rng, [answerId, ...others.slice(0, 7)])
  // 📊 v4.98 (per Javi: "stat options should match the name options"):
  // 8 statlines, like the 8 names. Decoys come from creatures OUTSIDE
  // the name choices and must be DISTINCT statlines — with seven of
  // them, a duplicate of the answer's line would make two "right"
  // buttons where only the seeded index pays.
  const answerStats = { ...getCreatureDef(answerId).stats }
  const seen = new Set([JSON.stringify(answerStats)])
  const statDecoys: string[] = []
  for (const id of others.slice(7)) {
    if (statDecoys.length >= choices.length - 1) break
    const key = JSON.stringify(getCreatureDef(id).stats)
    if (seen.has(key)) continue
    seen.add(key)
    statDecoys.push(id)
  }
  const statChoices = shuffled(rng, [
    answerStats,
    ...statDecoys.map(id => ({ ...getCreatureDef(id).stats })),
  ])
  const statAnswer = statChoices.findIndex(s =>
    JSON.stringify(s) === JSON.stringify(answerStats))
  return { day: dailyKey(date), answerId, choices, statChoices, statAnswer }
}

/** Stardust for solving on guess N (1-based); miss pays the consolation. */
export function guessReward(guessesUsed: number, solved: boolean): number {
  if (!solved) return TUNING.daily.missReward
  return TUNING.daily.guessRewards[guessesUsed - 1] ?? TUNING.daily.missReward
}

export interface StreakResult {
  streak: number
  milestoneStardust: number
  boosterEarned: boolean
}

/**
 * Completing a day advances the streak (a PLAYED day counts, win or
 * miss — streaks punish absence, not a bad guess). Milestones pay when
 * the streak lands exactly on them; every `boosterEvery`th day queues a
 * free pack.
 */
export function advanceStreak(prevStreak: number, lastDoneDay: string | null, today: Date): StreakResult {
  const streak = lastDoneDay === yesterdayKey(today) ? prevStreak + 1 : 1
  const milestoneStardust = TUNING.daily.milestones[streak] ?? 0
  const boosterEarned = streak > 0 && streak % TUNING.daily.boosterEvery === 0
  return { streak, milestoneStardust, boosterEarned }
}
